import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Check, Loader2, Store, AlertCircle, CheckCircle2, Info } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";
import { getAnnualPlans, getMonthlyPlans } from "@/components/pricing/pricingData";
import {
  PLAN_TYPE_TO_SLUG,
  pickMerchantStore,
  subscriptionErrorMessage,
  type MerchantStore,
  type PricingPlanType,
} from "@/lib/subscriptionPlans";

type Notice = { kind: "success" | "info" | "error"; title: string; text: string } | null;

/**
 * Post-trial subscription for the merchant's EXISTING store.
 * Prices: pricingData (single source) + site currency converter. Payment: create-subscription-checkout
 * only (Stripe test, no new trial). This page never writes any store data.
 */
export default function Abonnement() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { formatSubscriptionPrice } = useCurrency();

  const [isAnnual, setIsAnnual] = useState(false);
  const [store, setStore] = useState<MerchantStore | null>(null);
  const [planIds, setPlanIds] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<PricingPlanType | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const plans = useMemo(() => {
    const monthly = getMonthlyPlans();
    return isAnnual ? getAnnualPlans(monthly) : monthly;
  }, [isAnnual]);

  // Return from Stripe
  useEffect(() => {
    const status = params.get("subscription");
    if (!status) return;
    if (status === "success") {
      setNotice({
        kind: "success",
        title: "Paiement reçu",
        text: "Merci ! La confirmation de votre abonnement est en cours. Votre boutique reste inchangée.",
      });
    } else if (status === "canceled") {
      setNotice({
        kind: "info",
        title: "Paiement interrompu",
        text: "Aucun montant n'a été débité et votre boutique est inchangée. Vous pouvez choisir un forfait à tout moment.",
      });
    }
    const next = new URLSearchParams(params);
    next.delete("subscription");
    next.delete("session_id");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read-only: the merchant's real store and the plan ids
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/connexion"); return; }
      const [settings, stores, plansRes] = await Promise.all([
        supabase.from("store_settings").select("id, store_name, created_at").eq("user_id", user.id),
        supabase.from("stores").select("id, store_name, created_at").eq("owner_id", user.id),
        supabase.from("plans").select("id, slug").eq("is_active", true).in("slug", Object.values(PLAN_TYPE_TO_SLUG)),
      ]);
      if (cancelled) return;
      setStore(pickMerchantStore((settings.data as any[]) ?? [], (stores.data as any[]) ?? []));
      const ids: Record<string, string> = {};
      ((plansRes.data as any[]) ?? []).forEach((p) => { ids[p.slug] = p.id; });
      setPlanIds(ids);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const subscribe = async (planType: PricingPlanType) => {
    const planId = planIds[PLAN_TYPE_TO_SLUG[planType]];
    if (!store || !planId) {
      setNotice({ kind: "error", title: "Paiement impossible", text: subscriptionErrorMessage(!store ? "FORBIDDEN" : "PLAN_NOT_FOUND") });
      return;
    }
    setPending(planType);
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("create-subscription-checkout", {
        body: { planId, storeId: store.id, billingCycle: isAnnual ? "yearly" : "monthly", returnTo: "abonnement" },
      });
      if (error) {
        let code: string | undefined;
        try { code = (await (error as any)?.context?.json?.())?.error; } catch { /* ignore */ }
        throw new Error(code ?? "CHECKOUT_FAILED");
      }
      if (!data?.url) throw new Error("CHECKOUT_FAILED");
      window.location.href = data.url;
    } catch (e: any) {
      setNotice({ kind: "error", title: "Paiement impossible", text: subscriptionErrorMessage(e?.message) });
      setPending(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 sm:py-20 max-w-6xl space-y-10">
        <header className="text-center space-y-4 max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Continuez avec votre boutique</h1>
          <p className="text-muted-foreground">
            Votre essai gratuit est terminé. Choisissez un forfait pour retrouver l'accès à votre tableau de bord.
          </p>
          {store ? (
            <div className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm">
              <Store className="h-4 w-4 text-primary" />
              <span>Boutique : <strong translate="no">{store.name}</strong></span>
            </div>
          ) : (
            <p className="text-sm text-destructive">Aucune boutique trouvée pour votre compte.</p>
          )}
        </header>

        {notice && (
          <Alert variant={notice.kind === "error" ? "destructive" : "default"} className="max-w-2xl mx-auto">
            {notice.kind === "success" ? <CheckCircle2 className="h-4 w-4" /> : notice.kind === "error" ? <AlertCircle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
            <AlertTitle>{notice.title}</AlertTitle>
            <AlertDescription>{notice.text}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-center gap-3">
          <span className={`text-sm font-medium ${!isAnnual ? "text-foreground" : "text-muted-foreground"}`}>Mensuel</span>
          <Switch checked={isAnnual} onCheckedChange={setIsAnnual} aria-label="Facturation annuelle" />
          <span className={`text-sm font-medium ${isAnnual ? "text-foreground" : "text-muted-foreground"}`}>Annuel</span>
          <Badge variant="secondary" className="text-xs">2 mois gratuits</Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card
              key={plan.planType}
              className={`relative flex flex-col p-6 sm:p-8 ${plan.popular ? "border-2 border-primary shadow-lg" : ""}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Le plus populaire</Badge>
              )}
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">{plan.name}</h2>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-1 pt-2">
                  <span className="text-4xl font-extrabold" translate="no">{formatSubscriptionPrice(Number(plan.price))}</span>
                  <span className="text-sm text-muted-foreground">{plan.period}</span>
                </div>
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full mt-8"
                variant={plan.popular ? "default" : "outline"}
                disabled={!store || pending !== null}
                onClick={() => subscribe(plan.planType)}
              >
                {pending === plan.planType && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Choisir {plan.name}
              </Button>
            </Card>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground max-w-xl mx-auto">
          Aucun nouvel essai gratuit : le premier paiement a lieu dès la validation sur la page sécurisée Stripe.
          Le choix d'un forfait ne modifie ni le nom, ni l'adresse, ni les produits, ni les réglages de votre boutique.
        </p>
      </div>
    </div>
  );
}
